import React, { useState, useEffect } from 'react';

import { Form, Card, Col, Row, Empty, Button, message, notification, Typography, Input, Popconfirm } from 'antd';

import { FileUnknownOutlined, ArrowLeftOutlined, EditOutlined, CheckOutlined } from '@ant-design/icons';

import { Price, ErrorRender, FormErrorParser } from '@partsbadger/library';

import QuoteStore from '../../../stores/QuoteStore';
import UserStore from '../../../stores/UserStore';
import SalesOrderStore from '../../../stores/SalesOrderStore';

import { PageHeader } from 'components/PageHeader';

import { Link, useHistory, useParams } from 'react-router-dom';

import { observer } from 'mobx-react';
import PartStore from 'stores/PartStore';

const { Title } = Typography;

const GroupDetail: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [rename, setRename] = useState<boolean>(false);

    const [form] = Form.useForm();

    const { id } = useParams<{ id: string }>();

    const history = useHistory();

    const getGroupDetails = async () => {
        const params = {};
        await PartStore.getPartsGroupById(parseInt(id)).catch((err: any) => {
            notification.error({
                message: <ErrorRender error={err} />,
                placement: 'topRight',
            });
        });
    };

    useEffect(() => {
        getGroupDetails();
    }, []);

    const goCheckoutPage = async () => {
        setLoading(true);

        if (!UserStore.user?.default_contact?.id) {
            return message.error('No contact provided');
        }

        const payload = {
            contact: UserStore.user.default_contact.id,
            line_items: PartStore?.partsGroupDetails?.line_items?.map(item => {
                return {
                    quantity: item.quantity,
                    name: item.name,
                    quote_has_product: item.id,
                    product_description: '',
                    unit_price: item.unit_price,
                };
            }),
        };

        await SalesOrderStore.createOrderDraft(payload)
            .then(order => {
                history.push(`/my-account/orders/checkout/${order.id}/`);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'bottomRight',
                });

                setLoading(false);
            });
    };

    const sumSubTotal = PartStore?.partsGroupDetails?.line_items?.reduce(
        (accumulator, currentValue) => accumulator + parseFloat(currentValue.total_price),
        0
    );

    const handleDelete = async (quoteId: number) => {
        const payload = {
            line_item_id: quoteId,
        };

        await PartStore.deletePartFromGroup(parseInt(id), payload);

        notification.success({
            duration: 3,
            message: 'Part remove from the Group',
            description: 'Your item has been removed from the Group.',
            placement: 'topRight',
        });
    };

    const handleSubmit = () => {
        setLoading(true);
        form.validateFields()
            .then(async values => {
                const payload = {
                    name: values.group_name,
                };

                await PartStore.updatePartsGroup(parseInt(id), payload)
                    .then(() => {
                        notification.success({
                            message: 'Updated Successfully',
                            description: 'The group name was updated successfully.',
                            placement: 'topRight',
                        });
                    })
                    .catch((err: any) => {
                        notification.error({
                            message: <ErrorRender error={err} />,
                            placement: 'topRight',
                        });
                    });

                setLoading(false);
                setRename(false);
            })
            .catch(errorInfo => {
                setLoading(false);

                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            });
    };

    return (
        <>
            <PageHeader>
                <Title level={1}>
                    {rename ? (
                        <>
                            <Form form={form}>
                                <Form.Item
                                    label="Group Name"
                                    // {...formItemLayout}
                                    name="group_name"
                                    rules={[{ required: true, message: 'Please input a Group Name' }]}
                                    initialValue={PartStore?.partsGroupDetails?.name}
                                >
                                    <div>
                                        <Input
                                            minLength={5}
                                            maxLength={200}
                                            placeholder="Please enter the group name"
                                            style={{ width: 220 }}
                                            defaultValue={PartStore?.partsGroupDetails?.name}
                                        />
                                        <Button
                                            icon={<CheckOutlined />}
                                            type="link"
                                            onClick={() => {
                                                handleSubmit();
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form.Item>
                            </Form>
                        </>
                    ) : (
                        <>
                            {PartStore?.partsGroupDetails?.name}
                            <Button
                                icon={<EditOutlined />}
                                type="link"
                                onClick={() => {
                                    setRename(true);
                                }}
                            >
                                Rename
                            </Button>
                        </>
                    )}
                </Title>
                <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => {
                        history.goBack();
                    }}
                    className="text-primary m-0 p-0"
                >
                    Back
                </Button>
            </PageHeader>
            <div className="container">
                {!PartStore?.partsGroupDetails?.line_items?.length ? (
                    <>
                        <Empty
                            image={null}
                            description={<span className="font-size-lg">Your Group is Empty</span>}
                        ></Empty>
                    </>
                ) : (
                    <Row gutter={[16, 0]}>
                        <Col xl={16} lg={14} span={24}>
                            {PartStore?.partsGroupDetails.line_items?.map((item, index) => {
                                return (
                                    <Card className="shadow-sm mb-2" key={item.id} bodyStyle={{ padding: '1rem' }}>
                                        <div className="d-flex flex-xl-row flex-column justify-content-between w-100">
                                            <div className="d-flex flex-row">
                                                <div className="mr-3">
                                                    {item?.product?.thumbnail ? (
                                                        <div className="d-flex flex-column justify-content-center align-items-center">
                                                            <img
                                                                className="thumbnail-3d-file"
                                                                style={{
                                                                    width: '150px',
                                                                    height: '130px',
                                                                    display: 'flex',
                                                                    margin: 'auto',
                                                                    justifyContent: 'center',
                                                                }}
                                                                src={item?.product?.thumbnail}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <FileUnknownOutlined
                                                            style={{
                                                                fontSize: 120,
                                                                display: 'flex',
                                                                margin: 'auto',
                                                                justifyContent: 'center',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="d-flex flex-column justify-content-between">
                                                    <div className="d-flex flex-column">
                                                        <span className="font-size-md font-weight-semibold mb-2">
                                                            {item.name}
                                                        </span>
                                                        <span>
                                                            <span className="font-weight-semibold">Material:</span>{' '}
                                                            <span>{item.product.material_related.name}</span>
                                                        </span>
                                                        <span>
                                                            <span className="font-weight-semibold">Finish:</span>{' '}
                                                            <span>{item.product.finish_related.name}</span>
                                                        </span>
                                                        <span>
                                                            <span className="font-weight-semibold">Coating:</span>{' '}
                                                            <span>{item.product.coating_related.name}</span>
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="d-flex flex-row align-items-center"
                                                        style={{ columnGap: '6px' }}
                                                    >
                                                        <Popconfirm
                                                            title="Are you sure to delete this part?"
                                                            onConfirm={() => {
                                                                handleDelete(item.id);
                                                            }}
                                                            okText="Yes"
                                                            cancelText="No"
                                                        >
                                                            <span className="link underline text-red-600">Remove</span>
                                                        </Popconfirm>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex flex-xl-column-reverse flex-row text-right justify-content-between align-items-end">
                                                <div className="quantity-selector d-flex flex-row align-items-center justify-content-end">
                                                    <div className="d-flex flex-row align-items-center">
                                                        <label
                                                            htmlFor="quantity-input"
                                                            className="ant-form-item-label mr-2"
                                                        >
                                                            Qty:
                                                        </label>
                                                        <div className="button-group">
                                                            <Form.Item noStyle>
                                                                <span>{item.quantity}</span>
                                                            </Form.Item>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    className="d-flex flex-column justify-content-center ml-3 mb-xl-3"
                                                    style={{ minWidth: '100px' }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: '1rem',
                                                            fontWeight: 400,
                                                            paddingLeft: '0.5rem',
                                                        }}
                                                    >
                                                        <Price value={item.unit_price} /> ea.
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontSize: '1.3rem',
                                                            fontWeight: 500,
                                                            paddingLeft: '0.5rem',
                                                        }}
                                                    >
                                                        <Price value={item.total_price} />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </Col>
                        <Col xl={8} lg={10} span={24}>
                            <Card
                                title={
                                    <div className="d-flex flex-row justify-content-between">
                                        <span className="font-size-lg font-weight-normal">
                                            Subtotal ({PartStore?.partsGroupDetails?.line_items?.length}{' '}
                                            {PartStore?.partsGroupDetails?.line_items?.length === 1 ? 'Item' : 'Items'}
                                            ):
                                        </span>
                                        <span className="font-size-lg font-weight-semibold">
                                            <Price value={sumSubTotal ? sumSubTotal : 0} />
                                        </span>
                                    </div>
                                }
                                className="shadow-sm"
                            >
                                <Button
                                    id="quickturn-continue-to-checkout"
                                    type="primary"
                                    size="large"
                                    block
                                    onClick={async () => {
                                        await goCheckoutPage();
                                        QuoteStore.cleanSelectedLineItems();
                                    }}
                                >
                                    Proceed to Checkout
                                </Button>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </>
    );
};

export default observer(GroupDetail);
